import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChatBubbleLeftEllipsisIcon, PaperAirplaneIcon, ExclamationTriangleIcon, TrashIcon, PlusIcon, Bars3Icon, XMarkIcon, RectangleStackIcon } from '@heroicons/react/24/outline'
import type { Application, SubscriptionPlan, DateFormat } from '../types'
import { sendChatMessage, loadChatHistory, saveChatMessage, deleteConversation, getChatConversations, createConversation, type ChatMessage, type ChatConversationPreview } from '../services/aiChatService'
import { UpgradePrompt } from './UpgradePrompt'
import { formatDate } from '../utils/dateFormat'
import { trackAIChatStart, trackAIChatMessage } from '../utils/facebookPixel'
import { trackGAAIChatStart, trackGAAIChatMessage } from '../utils/googleAnalytics'

type Props = {
  userId: string
  plan: SubscriptionPlan
  applications: Application[]
  dateFormat: DateFormat
}

// Streaming config: words per chunk and delay between chunks (ms)
const WORDS_PER_CHUNK = 4
const CHUNK_DELAY = 40

export function AIChatPage({ plan, applications, dateFormat }: Props) {
  const [searchParams] = useSearchParams()
  const preSelectedAppId = searchParams.get('app')

  const [selectedAppId, setSelectedAppId] = useState<string>(preSelectedAppId || '')
  const [selectedConversationId, setSelectedConversationId] = useState<string>('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Streaming/typing animation state
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null)
  const [displayedContent, setDisplayedContent] = useState<string>('')
  const streamingRef = useRef<{ words: string[]; wordIndex: number } | null>(null)
  
  // Chat history sidebar state
  const [conversations, setConversations] = useState<ChatConversationPreview[]>([])
  const [isLoadingConversations, setIsLoadingConversations] = useState(false)
  const [showNewChatSelector, setShowNewChatSelector] = useState(false)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const isPro = plan === 'pro'

  // All non-archived apps for the dropdown
  const sortedApps = useMemo(() =>
    [...applications]
      .filter(a => !a.archived)
      .sort((a, b) => a.company.localeCompare(b.company)),
    [applications]
  )

  const selectedApp = useMemo(() =>
    applications.find(a => a.id === selectedAppId) || null,
    [applications, selectedAppId]
  )

  const hasJobContent = Boolean(selectedApp?.rawMarkdown?.trim())

  // Get app info for conversation preview
  const getAppForConversation = useCallback((appId: string) => 
    applications.find(a => a.id === appId),
    [applications]
  )

  // Load conversations on mount
  useEffect(() => {
    if (!isPro) return
    setIsLoadingConversations(true)
    getChatConversations()
      .then(convos => setConversations(convos))
      .catch(err => console.error('Failed to load conversations:', err))
      .finally(() => setIsLoadingConversations(false))
  }, [isPro])

  // Pre-select from URL - creates a new conversation for that app
  useEffect(() => {
    if (preSelectedAppId && sortedApps.some(a => a.id === preSelectedAppId)) {
      // Check if there's an existing conversation for this app, otherwise start new chat flow
      const existingConvo = conversations.find(c => c.applicationId === preSelectedAppId)
      if (existingConvo) {
        handleSelectConversation(existingConvo.id)
      } else if (!isLoadingConversations) {
        handleCreateNewChatForApp(preSelectedAppId)
      }
    }
  }, [preSelectedAppId, sortedApps, conversations, isLoadingConversations])

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
  }, [messages])

  // Focus input on app selection
  useEffect(() => {
    if (selectedAppId && inputRef.current) {
      inputRef.current.focus()
    }
  }, [selectedAppId])

  // Word-chunk streaming animation effect (like ChatGPT)
  useEffect(() => {
    if (!streamingMessageId || !streamingRef.current) return

    const { words, wordIndex } = streamingRef.current
    
    if (wordIndex >= words.length) {
      // Animation complete
      setStreamingMessageId(null)
      streamingRef.current = null
      return
    }

    const timer = setTimeout(() => {
      const nextIndex = Math.min(wordIndex + WORDS_PER_CHUNK, words.length)
      streamingRef.current = { words, wordIndex: nextIndex }
      setDisplayedContent(words.slice(0, nextIndex).join(' '))
    }, CHUNK_DELAY)

    return () => clearTimeout(timer)
  }, [streamingMessageId, displayedContent])

  // Scroll during streaming
  useEffect(() => {
    if (streamingMessageId) {
      messagesEndRef.current?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
    }
  }, [displayedContent, streamingMessageId])

  // Select a conversation (from sidebar)
  const handleSelectConversation = async (conversationId: string) => {
    const conversation = conversations.find(c => c.id === conversationId)
    if (!conversation) return

    setSelectedConversationId(conversationId)
    setSelectedAppId(conversation.applicationId || 'general')  // 'general' for null applicationId
    setMessages([])
    setError(null)
    setStreamingMessageId(null)
    streamingRef.current = null
    setShowNewChatSelector(false)
    setSidebarOpen(false)

    // Track chat start
    if (conversation.applicationId) {
      const app = applications.find(a => a.id === conversation.applicationId)
      if (app) {
        const hasContent = Boolean(app.rawMarkdown?.trim())
        trackAIChatStart(conversation.applicationId, hasContent)
        trackGAAIChatStart(conversation.applicationId, hasContent)
      }
    } else {
      // General chat
      trackAIChatStart('general', true)
      trackGAAIChatStart('general', true)
    }

    // Load conversation history
    setIsLoadingHistory(true)
    try {
      const history = await loadChatHistory(conversationId)
      setMessages(history)
    } catch (err) {
      console.error('Failed to load chat history:', err)
    } finally {
      setIsLoadingHistory(false)
    }
  }

  // Create a new conversation for an application
  const handleCreateNewChatForApp = async (appId: string) => {
    setShowNewChatSelector(false)
    setSidebarOpen(false)
    setSelectedAppId(appId)
    setMessages([])
    setError(null)
    setStreamingMessageId(null)
    streamingRef.current = null

    // Track chat start
    const app = applications.find(a => a.id === appId)
    if (app) {
      const hasContent = Boolean(app.rawMarkdown?.trim())
      trackAIChatStart(appId, hasContent)
      trackGAAIChatStart(appId, hasContent)
    }

    // Create the conversation in the database
    try {
      const newConversation = await createConversation(appId)
      setSelectedConversationId(newConversation.id)
      
      // Add to conversations list
      setConversations(prev => [{
        id: newConversation.id,
        applicationId: appId,
        title: null,
        lastMessage: '',
        lastMessageAt: new Date(),
        messageCount: 0,
      }, ...prev])
    } catch (err) {
      console.error('Failed to create conversation:', err)
      setError('Failed to create conversation')
    }
  }

  // Create a general chat (all applications context)
  const handleCreateGeneralChat = async () => {
    setShowNewChatSelector(false)
    setSidebarOpen(false)
    setSelectedAppId('general')  // Use 'general' as marker for general chat
    setMessages([])
    setError(null)
    setStreamingMessageId(null)
    streamingRef.current = null

    // Track chat start for general chat
    trackAIChatStart('general', true)
    trackGAAIChatStart('general', true)

    // Create the conversation in the database with null applicationId
    try {
      const newConversation = await createConversation(null, 'General Chat')
      setSelectedConversationId(newConversation.id)
      
      // Add to conversations list
      setConversations(prev => [{
        id: newConversation.id,
        applicationId: null,
        title: 'General Chat',
        lastMessage: '',
        lastMessageAt: new Date(),
        messageCount: 0,
      }, ...prev])
    } catch (err) {
      console.error('Failed to create conversation:', err)
      setError('Failed to create conversation')
    }
  }

  // Start new chat - show app selector
  const handleStartNewChat = () => {
    setShowNewChatSelector(true)
    setSelectedAppId('')
    setSelectedConversationId('')
    setMessages([])
    setError(null)
  }

  // Delete conversation
  const handleDeleteConversation = async () => {
    if (!selectedConversationId || messages.length === 0) return
    
    if (!confirm('Are you sure you want to delete this conversation? This cannot be undone.')) {
      return
    }

    try {
      await deleteConversation(selectedConversationId)
      setMessages([])
      // Remove from conversations list
      setConversations(prev => prev.filter(c => c.id !== selectedConversationId))
      // Clear selection
      setSelectedConversationId('')
      setSelectedAppId('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete conversation')
    }
  }

  const handleSendMessage = useCallback(async () => {
    if (!selectedAppId || !selectedConversationId || !inputValue.trim() || isLoading || streamingMessageId) return

    const userMessageContent = inputValue.trim()
    
    // Optimistically add user message to UI
    const tempUserMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessageContent,
      timestamp: new Date(),
    }

    setMessages(prev => [...prev, tempUserMessage])
    setInputValue('')
    setIsLoading(true)
    setError(null)

    try {
      // Save user message to database (fire-and-forget pattern)
      saveChatMessage(selectedConversationId, 'user', userMessageContent).catch(err => 
        console.error('Failed to save user message:', err)
      )

      // Pass null for applicationId when it's a general chat
      const apiApplicationId = selectedAppId === 'general' ? null : selectedAppId
      const response = await sendChatMessage(apiApplicationId, userMessageContent, messages)

      // Create assistant message with full content
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.message,
        timestamp: new Date(),
      }

      // Add message to state (will show with typing animation)
      setMessages(prev => [...prev, assistantMessage])
      
      // Start word-chunk streaming animation
      const words = response.message.split(/\s+/)
      streamingRef.current = { words, wordIndex: 0 }
      setDisplayedContent('')
      setStreamingMessageId(assistantMessage.id)

      // Save assistant message to database (fire-and-forget)
      saveChatMessage(selectedConversationId, 'assistant', response.message).catch(err =>
        console.error('Failed to save assistant message:', err)
      )
      
      // Update conversations list
      setConversations(prev => {
        const existing = prev.find(c => c.id === selectedConversationId)
        if (existing) {
          return [
            { ...existing, lastMessage: response.message.slice(0, 80), lastMessageAt: new Date(), messageCount: existing.messageCount + 2 },
            ...prev.filter(c => c.id !== selectedConversationId)
          ]
        } else {
          return [
            { id: selectedConversationId, applicationId: selectedAppId, title: null, lastMessage: response.message.slice(0, 80), lastMessageAt: new Date(), messageCount: 2 },
            ...prev
          ]
        }
      })

      // Track message
      trackAIChatMessage(selectedAppId, messages.length + 2)
      trackGAAIChatMessage(selectedAppId, messages.length + 2)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
    } finally {
      setIsLoading(false)
    }
  }, [selectedAppId, selectedConversationId, inputValue, isLoading, streamingMessageId, messages])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Feature gate for free users
  if (!isPro) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header - consistent with other pages */}
        <div className="flex items-center gap-3">
          <ChatBubbleLeftEllipsisIcon className="h-8 w-8 text-sky-500 dark:text-app-accent" />
          <div>
            <h1 className="text-2xl font-bold text-app-ink font-display">AI Assistant</h1>
            <p className="text-sm text-app-muted">Your AI job search assistant</p>
          </div>
        </div>
        <div className="card p-6">
          <UpgradePrompt type="feature" featureName="AI Assistant" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header - consistent with other pages */}
      <div className="flex items-center gap-3">
        <ChatBubbleLeftEllipsisIcon className="h-8 w-8 text-sky-500 dark:text-app-accent" />
        <div>
          <h1 className="text-2xl font-bold text-app-ink font-display">AI Assistant</h1>
          <p className="text-sm text-app-muted">Your AI job search assistant</p>
        </div>
      </div>

      {/* Main content: sidebar + chat area */}
      <div className="flex gap-6 min-h-[600px]">
        {/* Mobile sidebar toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-20 right-4 z-40 p-2 rounded-lg bg-app-surface border border-app-line shadow-lg"
        >
          {sidebarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
        </button>

        {/* Sidebar with conversations */}
        <div className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
 lg:translate-x-0
 fixed lg:static inset-y-0 left-0 z-30
 w-64 flex-shrink-0
 transition-transform duration-200 ease-in-out
 flex flex-col h-full lg:h-auto lg:max-h-[600px]
 bg-app-surface
 lg:border lg:border-app-line  lg:rounded-xl dark:lg:rounded-2xl lg:shadow-app-card
`}>
          {/* New Chat button */}
          <div className="p-3 border-b border-app-line">
            <button
              onClick={handleStartNewChat}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-lg bg-app-primary text-white hover:bg-app-primary-hover transition-colors text-sm font-medium"
            >
              <PlusIcon className="h-5 w-5" />
              New Chat
            </button>
          </div>

            {/* Conversations list */}
            <div className="flex-1 overflow-y-auto py-2 px-2 space-y-0.5">
              {isLoadingConversations ? (
                <div className="flex justify-center py-8">
                  <div className="flex gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <p className="text-sm text-app-muted">
                    No conversations yet
                  </p>
                </div>
              ) : (
                conversations.map(convo => {
                  const isGeneralChat = !convo.applicationId
                  const app = isGeneralChat ? null : getAppForConversation(convo.applicationId!)
                  const isSelected = convo.id === selectedConversationId

                  return (
                    <button
                      key={convo.id}
                      onClick={() => handleSelectConversation(convo.id)}
                      className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                        isSelected
                          ? 'bg-app-highlight'
                          : 'hover:bg-app-subtle'
                      }`}
                    >
                      <p className={`text-sm font-medium truncate ${
                        isSelected ? 'text-sky-700 dark:text-app-accent' : 'text-app-ink'
                      }`}>
                        {isGeneralChat ? (
                          <span className="inline-flex items-center gap-1">
                            <RectangleStackIcon className="h-4 w-4" />
                            All Applications
                          </span>
                        ) : (app?.company || 'Unknown')}
                      </p>
                      <p className="text-xs text-app-muted truncate">
                        {convo.lastMessage || (isGeneralChat ? 'Portfolio overview' : (app?.positionTitle || 'New conversation'))}
                      </p>
                    </button>
                  )
                })
              )}
            </div>
        </div>

        {/* Main chat area */}
        <div className="flex-1 card flex flex-col min-w-0 min-h-[600px]">
          {/* Context header - shows selected conversation info */}
          {(selectedAppId || selectedConversationId) && (
            <div className="flex-shrink-0 px-4 py-3 border-b border-app-line flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                {selectedAppId === 'general' ? (
                  <>
                    <RectangleStackIcon className="h-5 w-5 text-sky-500 dark:text-app-accent flex-shrink-0" />
                    <span className="text-sm font-medium text-app-ink">All Applications</span>
                  </>
                ) : selectedApp ? (
                  <>
                    <span className="text-sm font-medium text-app-ink truncate">
                      {selectedApp.company}
                    </span>
                    {selectedApp.positionTitle && (
                      <span className="text-xs text-app-muted truncate hidden sm:inline">
                        — {selectedApp.positionTitle}
                      </span>
                    )}
                  </>
                ) : null}
              </div>
              
              {selectedConversationId && messages.length > 0 && (
                <button
                  onClick={handleDeleteConversation}
                  title="Delete conversation"
                  className="flex-shrink-0 p-2 rounded-lg text-app-muted hover:text-red-500 hover:bg-app-subtle transition-colors"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

        {/* Chat area */}
        <div className="flex-1 overflow-y-auto px-4 py-6 lg:px-6">
          {!selectedAppId && !showNewChatSelector ? (
            /* Initial empty state - show recent chats prompt */
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-app-page">
                <ChatBubbleLeftEllipsisIcon className="h-8 w-8 text-app-muted" />
              </div>
              <h2 className="text-lg font-medium text-app-ink mb-2 font-display">
                Welcome to AI Assistant
              </h2>
              <p className="text-sm text-app-muted max-w-md mb-6">
                {conversations.length > 0 
                  ? 'Select a conversation from the sidebar to continue, or start a new chat.'
                  : 'Start a new chat to get AI-powered help with your job applications.'}
              </p>
              <button
                onClick={handleStartNewChat}
                className="flex items-center gap-2 rounded-lg bg-app-primary px-6 py-3 text-sm font-medium text-white hover:bg-app-primary-hover transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                Start New Chat
              </button>
            </div>
          ) : showNewChatSelector && !selectedAppId ? (
            /* New chat - select application */
            <div className="max-w-md mx-auto py-8">
              <h2 className="text-lg font-medium text-app-ink mb-2 text-center font-display">
                Start a New Chat
              </h2>
              <p className="text-sm text-app-muted text-center mb-6">
                Chat about all your applications, or select a specific one for focused assistance.
              </p>
              
              {/* General Chat option */}
              <button
                onClick={handleCreateGeneralChat}
                className="w-full text-left rounded-lg border-2 border-app-accent bg-app-highlight p-4 hover:bg-app-highlight transition-colors mb-4"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-sky-500 dark:bg-app-primary flex items-center justify-center">
                    <RectangleStackIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-sky-700 dark:text-app-accent">
                      All Applications
                    </p>
                    <p className="text-sm text-app-muted">
                      Get insights across your entire job search portfolio
                    </p>
                  </div>
                </div>
              </button>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-app-line" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-app-subtle px-2 text-app-muted">or choose an application</span>
                </div>
              </div>
              
              {/* Application dropdown */}
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    handleCreateNewChatForApp(e.target.value)
                  }
                }}
                className="w-full rounded-lg border border-app-line bg-app-surface px-4 py-3 text-sm text-app-ink focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent"
                defaultValue=""
              >
                <option value="" disabled>Select an application...</option>
                {sortedApps.map(app => {
                  const appConversations = conversations.filter(c => c.applicationId === app.id)
                  const chatCount = appConversations.length > 0 ? ` (${appConversations.length} chat${appConversations.length > 1 ? 's' : ''})` : ''
                  const hasDesc = app.rawMarkdown?.trim() ? ' ✓' : ''
                  return (
                    <option key={app.id} value={app.id}>
                      {app.company}{app.positionTitle ? ` — ${app.positionTitle}` : ''}{hasDesc}{chatCount}
                    </option>
                  )
                })}
              </select>

              {sortedApps.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-sm text-app-muted">
                    No applications found. Add some job applications first.
                  </p>
                </div>
              )}

              <button
                onClick={() => setShowNewChatSelector(false)}
                className="mt-4 w-full text-center text-sm text-app-muted hover:text-app-ink"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-4">
              {/* General chat info card */}
              {selectedAppId === 'general' && (
                <div className="mb-6 rounded-lg border border-sky-200 dark:border-app-line bg-sky-50 dark:bg-app-highlight p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-sky-500 dark:bg-app-primary flex items-center justify-center">
                      <RectangleStackIcon className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium text-sky-700 dark:text-app-accent font-display">
                        Portfolio Overview
                      </h3>
                      <p className="text-sm text-app-muted mt-1">
                        {sortedApps.length} active application{sortedApps.length !== 1 ? 's' : ''} loaded
                      </p>
                      <p className="text-xs text-app-muted mt-1">
                        Ask about your job search progress, compare offers, or get strategic advice.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected app info */}
              {selectedApp && selectedAppId !== 'general' && (
                <div className="mb-6 border border-app-line bg-app-subtle p-4 rounded-2xl shadow-app-card">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-app-ink font-display">
                        {selectedApp.company}
                      </h3>
                      {selectedApp.positionTitle && (
                        <p className="text-sm text-app-muted">
                          {selectedApp.positionTitle}
                        </p>
                      )}
                      <p className="text-xs text-app-muted mt-1">
                        Added {formatDate(selectedApp.createdAt, dateFormat)}
                      </p>
                    </div>
                    <Link
                      to={`/app/${selectedApp.id}`}
                      className="text-sm text-app-accent hover:underline"
                    >
                      View details
                    </Link>
                  </div>
                  {!hasJobContent && (
                    <div className="mt-3 flex items-start gap-2 text-amber-600 dark:text-amber-400">
                      <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                      <p className="text-sm">
                        No job description saved. The AI will have limited context.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Welcome message */}
              {messages.length === 0 && !isLoadingHistory && (
                <div className="text-center py-8">
                  <p className="text-app-muted">
                    {selectedAppId === 'general' 
                      ? 'Ask me about your job search progress!'
                      : 'Ask me anything about this job offer!'}
                  </p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2">
                    {(selectedAppId === 'general' ? [
                      'How is my job search going?',
                      'Which applications should I focus on?',
                      'Compare my offers',
                      'What should I do next?',
                    ] : [
                      'What are the key requirements?',
                      'What skills should I highlight?',
                      'Summarize this job posting',
                      'What questions should I prepare for?',
                    ]).map(suggestion => (
                      <button
                        key={suggestion}
                        onClick={() => {
                          setInputValue(suggestion)
                          inputRef.current?.focus()
                        }}
                        className="rounded-full border border-app-line bg-app-surface px-3 py-1.5 text-sm text-app-muted hover:border-app-accent hover:text-app-accent-hover transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading history indicator */}
              {isLoadingHistory && (
                <div className="text-center py-8">
                  <div className="flex justify-center gap-1.5 mb-2">
                    <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <p className="text-sm text-app-muted">Loading conversation...</p>
                </div>
              )}

              {/* Messages */}
              {messages.map(msg => {
                // Check if this message is currently being streamed
                const isStreamingThis = msg.id === streamingMessageId
                const contentToShow = isStreamingThis ? displayedContent : msg.content

                return (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-app-primary text-white'
                          : 'bg-app-page text-app-ink'
                      }`}
                    >
                      <p className="whitespace-pre-wrap text-sm">
                        {contentToShow}
                        {isStreamingThis && <span className="inline-block w-0.5 h-4 bg-current ml-0.5 animate-pulse" />}
                      </p>
                    </div>
                  </div>
                )
              })}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="max-w-[80%] rounded-2xl px-4 py-3 bg-app-page">
                    <div className="flex gap-1.5">
                      <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="h-2 w-2 rounded-full bg-app-surface dark:bg-app-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-600 dark:text-red-400">
                  {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        {selectedAppId && (
          <div className="flex-shrink-0 border-t border-app-line px-4 py-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex gap-3">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask a question about this job..."
                  rows={1}
                  disabled={isLoading || !!streamingMessageId}
                  className="flex-1 resize-none border border-app-line bg-app-surface px-4 py-3 text-sm text-app-ink placeholder:text-app-muted dark:placeholder:text-app-muted focus:border-app-accent focus:outline-none focus:ring-1 focus:ring-app-accent disabled:opacity-50 rounded-lg"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading || !!streamingMessageId}
                  className="flex-shrink-0 flex h-12 w-12 items-center justify-center bg-app-primary text-white hover:bg-app-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
                >
                  <PaperAirplaneIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="mt-2 text-xs text-app-muted text-center">
                Press Enter to send, Shift+Enter for new line
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  )
}
